import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import googleFill from '@iconify/icons-eva/google-fill';
import twitterFill from '@iconify/icons-eva/twitter-fill';
import facebookFill from '@iconify/icons-eva/facebook-fill';
import linkedinFill from '@iconify/icons-eva/linkedin-fill';
import shareOutline from '@iconify/icons-eva/share-outline';
import copyOutline from '@iconify/icons-eva/copy-outline';
import externalLinkOutline from '@iconify/icons-eva/external-link-outline';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// material
import { experimentalStyled as styled } from '@material-ui/core/styles';
import { Grid, Link, Divider, Container, Typography, IconButton, Stack, Box, Button, Menu, MenuItem, Tooltip } from '@material-ui/core';
// routes
import { PATH_PAGE } from '../../routes/paths';
//
import Logo from '../../components/Logo';
import LegalModal from '../../components/LegalModal';
import skynicheLogo from '../../images/skyniche-logo.png';

// ----------------------------------------------------------------------

const SOCIALS = [
  { name: 'FaceBook', icon: facebookFill },
  { name: 'Google', icon: googleFill },
  { name: 'Linkedin', icon: linkedinFill },
  { name: 'Twitter', icon: twitterFill }
];

const LOCATION_NAME = 'Mayura Vahanam';
const LOCATION_ADDRESS = '214, Thiyagi Kumaran Street, Coimbatore, 641001';

const LINKS = [
  {
    headline: 'Company',
    children: [
      { name: 'Home', action: 'home' },
      { name: 'Services', action: 'services' },
      { name: 'About us', href: PATH_PAGE.about },
      // { name: 'Contact us', href: PATH_PAGE.contact },
      { name: 'FAQs', href: '/#faqs' }
    ]
  },
  {
    headline: 'Legal',
    children: [
      { name: 'Terms and Condition', action: 'terms'},
      { name: 'Privacy Policy', action: 'privacy'}
    ]
  },
  {
    headline: 'Contact',
    children: [
      { name: LOCATION_NAME },
      { name: LOCATION_ADDRESS }
    ]
  }
];

// Lat/lng pin so the red marker always shows (address-only embed often centers without a pin)
const MAP_EMBED_URL =
  'https://maps.google.com/maps?q=11.0030774,76.953746+(Mayura+Vahanam)&z=18&output=embed';
const MAP_LINK =
  'https://www.google.com/maps/place/214,+Thiyaki+Kumaran+Rd,+Kollupuram,+R.S.+Puram,+Coimbatore,+Tamil+Nadu+641002/@11.0030774,76.953746,17z';

const RootStyle = styled('div')(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.background.default
}));

const MapWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 280,
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.customShadows?.z8 || '0 8px 16px rgba(0,0,0,0.08)',
  [theme.breakpoints.up('md')]: {
    height: 320
  }
}));

const MapInfoCard = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  left: 12,
  zIndex: 2,
  maxWidth: 280,
  padding: theme.spacing(1.5, 1.75),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: theme.palette.common.white,
  boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
  pointerEvents: 'auto',
  [theme.breakpoints.down('sm')]: {
    maxWidth: 'calc(100% - 24px)'
  }
}));

// ----------------------------------------------------------------------

export default function MainFooter() {
  const navigate = useNavigate();
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalType, setLegalType] = useState('terms');
  const [isSkynicheHovered, setIsSkynicheHovered] = useState(false);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
  const [copyTooltip, setCopyTooltip] = useState('Copy address');

  const handleOpenLegal = (type) => {
    setLegalType(type);
    setLegalModalOpen(true);
  };

  const handleHomeClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getShareText = () => `${LOCATION_NAME}\n${LOCATION_ADDRESS}\n${MAP_LINK}`;

  const copyToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  const handleCopyAddress = async () => {
    try {
      await copyToClipboard(getShareText());
      setCopyTooltip('Copied!');
      setTimeout(() => setCopyTooltip('Copy address'), 2000);
    } catch (err) {
      setCopyTooltip('Unable to copy');
      setTimeout(() => setCopyTooltip('Copy address'), 2000);
    }
  };

  return (
    <RootStyle>
      <Divider />
      <Container maxWidth="lg" sx={{ pt: 10 }}>
        <Grid
          container
          justifyContent={{ xs: 'center', md: 'space-between' }}
          sx={{ textAlign: { xs: 'center', md: 'left' } }}
        >
          <Grid item xs={12} sx={{ mb: 3 }}>
            <ScrollLink to="move_top" spy smooth>
              <Logo sx={{ mx: { xs: 'auto', md: 'inherit' } }} />
            </ScrollLink>
          </Grid>
          <Grid item xs={8} md={3}>
            <Typography variant="body2" sx={{ pr: { md: 5 } }}>
             Delivering reliable logistics solutions that connect businesses and customers with speed, security, and efficiency. Mayura Logistics — your trusted partner for seamless deliveries.
            </Typography>

            <Stack
              spacing={1.5}
              direction="row"
              justifyContent={{ xs: 'center', md: 'flex-start' }}
              sx={{ mt: 5, mb: { xs: 5, md: 0 } }}
            >
              {SOCIALS.map((social) => (
                <IconButton key={social.name} color="primary" sx={{ p: 1 }}>
                  <Icon icon={social.icon} width={16} height={16} />
                </IconButton>
              ))}
            </Stack>
            
          </Grid>

          <Grid item xs={12} md={7}>
            <Stack spacing={5} direction={{ xs: 'column', md: 'row' }} justifyContent="space-between">
              {LINKS.map((list) => {
                const { headline, children } = list;
                return (
                  <Stack key={headline} spacing={2}>
                    <Typography component="p" variant="overline">
                      {headline}
                    </Typography>
                    {children.map((link) => {
                      if (link.action) {
                        return (
                          <Link
                            key={link.name}
                            color="inherit"
                            variant="body2"
                            component="button"
                            onClick={(e) => {
                              if (link.action === 'terms') handleOpenLegal('terms');
                              if (link.action === 'privacy') handleOpenLegal('privacy');
                              if (link.action === 'home') handleHomeClick();
                              if (link.action === 'services') setServicesAnchorEl(e.currentTarget);
                            }}
                            sx={{ display: 'block', textAlign: { xs: 'center', md: 'left' }, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {link.name}
                          </Link>
                        );
                      }
                      return link.href ? (
                        <Link
                          to={link.href}
                          key={link.name}
                          color="inherit"
                          variant="body2"
                          component={RouterLink}
                          sx={{ display: 'block' }}
                        >
                          {link.name}
                        </Link>
                      ) : (
                        <Typography key={link.name} variant="body2" color="inherit" sx={{ display: 'block' }}>
                          {link.name}
                        </Typography>
                      );
                    })}
                  </Stack>
                );
              })}
            </Stack>
          </Grid>
        </Grid>

        <Box sx={{ mt: 8, mb: 2 }}>
          <Typography component="p" variant="overline" sx={{ mb: 2, display: 'block' }}>
            Our Location
          </Typography>
          <MapWrapper>
            <Box
              component="iframe"
              title="Mayura Vahanam location"
              src={MAP_EMBED_URL}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              sx={{
                border: 0,
                width: '100%',
                height: '100%',
                display: 'block'
              }}
            />
            <MapInfoCard>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>
                {LOCATION_NAME}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45, mb: 1.25 }}>
                {LOCATION_ADDRESS}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  href={MAP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<Icon icon={externalLinkOutline} width={14} height={14} />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 12,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' }
                  }}
                >
                  Open in Maps
                </Button>
                <Tooltip title={copyTooltip} arrow>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label="Copy address"
                    onClick={handleCopyAddress}
                  >
                    <Icon icon={copyOutline} width={18} height={18} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </MapInfoCard>
          </MapWrapper>
        </Box>

        <Box sx={{ mt: 10, display: 'flex', justifyContent: 'center' }}>
          <Button
            href="https://www.skyniche.com/"
            target="_blank"
            rel="noopener"
            startIcon={<Box component="img" src={skynicheLogo} sx={{ width: 24, height: 24 }} />}
            sx={{
              borderRadius: 50,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 13,
              px: 3,
              py: 1,
              color: 'primary.dark',
              bgcolor: 'rgba(60, 184, 120, 0.1)',
              border: '1px solid rgba(60, 184, 120, 0.2)',
              boxShadow: '0 4px 12px rgba(60, 184, 120, 0.05)',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'rgba(60, 184, 120, 0.15)',
                boxShadow: '0 8px 20px rgba(60, 184, 120, 0.2)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            A skyniche Production
          </Button>
        </Box>

        <Typography
          component="p"
          variant="body2"
          sx={{
            mt: 2,
            pb: 5,
            fontSize: 13,
            textAlign: 'center'
          }}
        >
          © 2026. All rights reserved
        </Typography>
      </Container>

      <LegalModal 
        open={legalModalOpen} 
        onClose={() => setLegalModalOpen(false)} 
        type={legalType} 
      />

      <Menu
        anchorEl={servicesAnchorEl}
        open={Boolean(servicesAnchorEl)}
        onClose={() => setServicesAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            ml: 1,
            boxShadow: (theme) => theme.customShadows.z20,
            borderRadius: 1.5,
            width: 200,
            border: '1px solid',
            borderColor: 'divider',
            p: 1
          }
        }}
      >
        <MenuItem 
          onClick={() => { 
            navigate('/services/national-delivery'); 
            setServicesAnchorEl(null); 
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{ 
            typography: 'subtitle2', 
            py: 1.5,
            px: 2,
            borderRadius: 1,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'primary.lighter',
              color: 'primary.dark',
              transform: 'translateX(4px)'
            }
          }}
        >
          National Delivery
        </MenuItem>
        <MenuItem 
          onClick={() => { 
            navigate('/services/local-delivery'); 
            setServicesAnchorEl(null); 
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{ 
            typography: 'subtitle2', 
            py: 1.5,
            px: 2,
            borderRadius: 1,
            mt: 0.5,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'primary.lighter',
              color: 'primary.dark',
              transform: 'translateX(4px)'
            }
          }}
        >
          Local Delivery
        </MenuItem>
      </Menu>
    </RootStyle>
  );
}
